import { collectionPrefix } from '../hooks'
import { CollectionProperty, DataObject, ResourceTypeConstant, ValueType } from './configTypes'
import { isUrl } from './helpers'

interface TypeErrorMessageProps {
  value: any
  property: CollectionProperty
  dataObjects: DataObject[]
}

const getAcceptValuesErrorMessage = (props: TypeErrorMessageProps) => {
  const {
    value,
    property: { acceptValues },
  } = props
  if (acceptValues && !acceptValues.find(el => el === value)) {
    return `Accepted values: ${acceptValues.join(', ')}!`
  }
  return false
}

const fieldTypes = [
  {
    type: ValueType.accessLevel,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null &&
      props.value !== undefined &&
      (getAcceptValuesErrorMessage(props) || (typeof props.value !== 'string' && 'Must be a string type!')),
  },
  {
    type: ValueType.string,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null &&
      props.value !== undefined &&
      (getAcceptValuesErrorMessage(props) || (typeof props.value !== 'string' && 'Must be a string type!')),
  },
  {
    type: ValueType.number,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null && props.value !== undefined && typeof props.value !== 'number' && 'Must be a number type!',
  },
  {
    type: ValueType.boolean,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null &&
      props.value !== undefined &&
      typeof props.value !== 'boolean' &&
      'Must be a boolean type!',
  },
  {
    type: ValueType.color,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null && props.value !== undefined && typeof props.value !== 'string' && 'Must be a string type!',
  },
  {
    type: ValueType.url,
    getTypeErrorMessage: (props: TypeErrorMessageProps) => {
      if (props.value !== null && props.value !== undefined) {
        try {
          if (!isUrl(props.value)) {
            return 'This is not valid URL!'
          }
        } catch {
          return 'This is not valid URL!'
        }
      }
      return false
    },
  },
  {
    type: ValueType.image,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null && props.value !== undefined && typeof props.value !== 'object' && 'Must be an object type!',
  },
  {
    type: ValueType.video,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null && props.value !== undefined && typeof props.value !== 'object' && 'Must be an object type!',
  },
  {
    type: ValueType.audio,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null && props.value !== undefined && typeof props.value !== 'object' && 'Must be an object type!',
  },
  {
    type: ValueType.file,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null && props.value !== undefined && typeof props.value !== 'object' && 'Must be an object type!',
  },
  {
    type: ValueType.record,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null &&
      props.value !== undefined &&
      !props.value.startsWith(collectionPrefix) &&
      'Invalid record!',
  },
  {
    type: ValueType.date,
    getTypeErrorMessage: (props: TypeErrorMessageProps) => {
      if (props.value !== null && props.value !== undefined) {
        try {
          if (isNaN(+new Date(props.value))) {
            return 'Invalid date!'
          }
        } catch {
          return 'Invalid date!'
        }
      }
      return false
    },
  },
  {
    type: ValueType.dateTime,
    getTypeErrorMessage: (props: TypeErrorMessageProps) => {
      if (props.value !== null && props.value !== undefined) {
        try {
          if (isNaN(+new Date(props.value))) {
            return 'Invalid date!'
          }
        } catch {
          return 'Invalid date!'
        }
      }
      return false
    },
  },
  {
    type: ValueType.richText,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null && props.value !== undefined && typeof props.value !== 'string' && 'Must be a string type!',
  },
  {
    type: ValueType.json,
    getTypeErrorMessage: (props: TypeErrorMessageProps) => {
      if (props.value !== null && props.value !== undefined) {
        try {
          JSON.parse(props.value)
        } catch {
          return 'This is not valid JSON!'
        }
      }
      return false
    },
  },
  {
    type: ValueType.keyValueMap,
    getTypeErrorMessage: (props: TypeErrorMessageProps) => {
      if (props.value !== null && props.value !== undefined) {
        if (typeof props.value !== 'object') {
          return 'Must be an object type!'
        }
        const { dataObject } = props.property
        if (dataObject) {
          const fields = Object.keys(props.value)
          const properties = props.dataObjects.find(el => el.name === dataObject)?.properties
          if (properties) {
            const needFields = properties.map(el => el.name)
            const missingFields = needFields.filter(el => !fields.includes(el))
            const extraFields = fields.filter(el => !needFields.includes(el))
            if (missingFields.length) {
              return `Missing fields: ${missingFields.join(', ')}!`
            } else if (extraFields.length) {
              return `Extra fields: ${extraFields.join(', ')}!`
            } else {
              const innerError = validate(props.value, properties, props.dataObjects)
              if (innerError) {
                return innerError
              }
            }
          }
        }
      }
      return false
    },
  },
  {
    type: ValueType.coordinate,
    getTypeErrorMessage: (props: TypeErrorMessageProps) => {
      if (props.value !== null && props.value !== undefined) {
        if (typeof props.value !== 'object') {
          return 'Must be an object type!'
        }
        const fields = Object.keys(props.value)
        const needFields = ['latitude', 'longitude']
        const missingFields = needFields.filter(el => !fields.includes(el))
        const extraFields = fields.filter(el => !needFields.includes(el))
        if (missingFields.length) {
          return `Missing fields: ${missingFields.join(', ')}!`
        } else if (extraFields.length) {
          return `Extra fields: ${extraFields.join(', ')}!`
        }
      }
      return false
    },
  },
  {
    type: ValueType.duration,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null && props.value !== undefined && typeof props.value !== 'string' && 'Must be a string type!',
  },
  {
    type: ValueType.currency,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null &&
      props.value !== undefined &&
      // @ts-ignore
      !Intl.supportedValuesOf('currency').find(el => el === props.value) &&
      // @ts-ignore
      `Accepted values: ${Intl.supportedValuesOf('currency').join(', ')}!`,
  },
  {
    type: ValueType.resourceType,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null &&
      props.value !== undefined &&
      !Object.values(ResourceTypeConstant).find(el => el !== props.value) &&
      `Accepted values: ${Object.values(ResourceTypeConstant).join(', ')}!`,
  },
  {
    type: ValueType.vector,
    getTypeErrorMessage: (props: TypeErrorMessageProps) =>
      props.value !== null && props.value !== undefined && typeof props.value !== 'string' && 'Must be a string type!',
  },
  {
    type: ValueType.array,
    getTypeErrorMessage: (props: TypeErrorMessageProps) => {
      if (props.value !== null && props.value !== undefined) {
        if (!Array.isArray(props.value)) {
          return 'Must be an array type!'
        }
        const { accept, dataObject, acceptValues } = props.property
        if (accept) {
          const properties: CollectionProperty[] = props.value.map((el: any, i: number) => ({
            position: i,
            name: `${i}`,
            type: accept,
            dataObject,
            acceptValues,
            isRequired: true,
          }))
          const innerError = validate(props.value, properties, props.dataObjects)
          if (innerError) {
            return innerError
          }
        }
      }
      return false
    },
  },
]

const getField = (type: ValueType) => fieldTypes.find(el => el.type === type)

export const validate = (
  values: any,
  properties: CollectionProperty[],
  dataObjects: DataObject[],
  isUpdate?: boolean
) => {
  const errors: { [key: string]: string } = {}
  ;(isUpdate
    ? Object.keys(values)
        .map(key => properties.find(property => property.name === key))
        .filter(el => !!el)
    : properties
  ).forEach(p => {
    const property = p as CollectionProperty
    const { name, type, isRequired } = property
    const value = values[name]
    if (value !== null) {
      const typeErrorMessage = getField(type)?.getTypeErrorMessage({ value, property, dataObjects })
      if (typeErrorMessage) {
        errors[name] = typeErrorMessage
      }
    } else if (isRequired) {
      errors[name] = 'Required!'
    }
  })
  if (Object.keys(errors).length) {
    return Object.keys(errors)
      .map(el => `Field "${el}": ${errors[el]}`)
      .join(' ')
  }
}
